import { useEffect, useRef } from "react";

/**
 * Iter-27.69 — Hook utilitaire pour sauvegarder un brouillon dans localStorage.
 *
 * Args :
 *   - key       : clé unique localStorage (ex: "shariti.draft.gratitude.amnesty")
 *   - state     : objet courant du composer
 *   - setState  : setter du state
 *   - isInitial : fonction (state) => true si on doit considérer le state comme "vide"
 *
 * Comportement :
 *   - Au mount : restore le draft si présent ET non-vide, déclenche le callback
 *     `onRestored(draft)` pour notifier le composant (toast par ex.)
 *   - À chaque modification : sauvegarde dans localStorage avec debounce 400ms
 *   - L'appelant doit appeler `clear()` après publication réussie pour vider le draft
 */
export default function useDraft(key, state, setState, { isInitial, onRestored } = {}) {
    const hydratedRef = useRef(false);
    const debounceRef = useRef(null);

    // Restore au mount
    useEffect(() => {
        if (hydratedRef.current) return;
        hydratedRef.current = true;
        try {
            const raw = localStorage.getItem(key);
            if (!raw) return;
            const draft = JSON.parse(raw);
            if (draft && !(isInitial ? isInitial(draft) : false)) {
                setState((curr) => ({ ...curr, ...draft }));
                onRestored?.(draft);
            }
        } catch {
            /* ignore */
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);

    // Save (debounced)
    useEffect(() => {
        if (!hydratedRef.current) return;
        if (isInitial && isInitial(state)) {
            // Si le composer est vide → on nettoie le draft
            try {
                localStorage.removeItem(key);
            } catch {
                /* ignore */
            }
            return;
        }
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
            try {
                localStorage.setItem(key, JSON.stringify(state));
            } catch {
                /* quota or disabled */
            }
        }, 400);
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [key, state, isInitial]);

    const clear = () => {
        try {
            localStorage.removeItem(key);
        } catch {
            /* ignore */
        }
    };

    return { clear };
}
